import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { ProductType } from 'components/shared/Cart/types';
import {
  RingSizes,
  RingType,
  SizingKitShopifyMap,
  RingProSizingKitShopifyMap,
  getRingAirColorNameImages,
  RingPriceData,
  getAvailableAirRingColors,
} from 'content/products/ring/productMap/constants';
import { productMap_air_ring } from 'content/products/ring';
import { RingProColors } from 'content/products/ring-pro/constants';
import { RingProVariants } from 'content/products/ring-pro/ringProVariants';
import { getRingProBasePrice } from 'content/products/ring-pro/tierPricing';
import { getRingProColorNameImages } from 'content/products/ring-pro/ringProColorNameImages';
import {
  DieselRingColors,
  DieselRingType,
  DieselRingPriceData,
  DieselRingColorNameImages,
  DieselRingColorNameLargeImages,
} from 'content/products/dieselRing/productMap/constants';
import { productMap_diesel_ring } from 'content/products/dieselRing/productMap';
import {
  RareColors,
  RarePriceData,
  RareSizingKitShopifyMap,
  RareColorNameImages,
} from 'content/products/rare/productMap/constants';
import { productMap_rare } from 'content/products/rare/productMap';
import {
  RingProChargingCaseBlackShopifyMap,
  RingProChargingCaseGoldShopifyMap,
} from 'content/products/accessories';
import type { ColorAccessoryVariant } from './ProductCardColorAccessory';
import type {
  ProductCardRingProps,
  RingColorVariant,
  RingSizeOption,
  RingSizingKitOption,
} from './ProductCardRing';

type TFunction = (key: string) => string;

const AIR_DESCRIPTION =
  "The world's most comfortable sleep tracker. Accurately measures your sleep, movement and recovery.";
const PRO_DESCRIPTION =
  'Our most advanced ring yet. A unibody fighter jet-grade titanium build — made for serious tracking.';
const DIESEL_DESCRIPTION =
  'An Ultrahuman x Diesel collaboration. Bold industrial designs in exclusive Cryo and Phantom finishes.';
const RARE_DESCRIPTION =
  "The world's first luxury smart ring. Crafted in 18K Gold and Platinum — for those who want the extraordinary.";

function airSizingKit(store: ShopifyStore): RingSizingKitOption | undefined {
  const id = SizingKitShopifyMap[store]?.shopifyVariantId;
  return typeof id === 'string' && id.length > 0
    ? { shopifyVariantId: id, productType: ProductType.RING }
    : undefined;
}

function proSizingKit(store: ShopifyStore): RingSizingKitOption | undefined {
  const id = RingProSizingKitShopifyMap[store]?.shopifyVariantId;
  return typeof id === 'string' && id.length > 0
    ? { shopifyVariantId: id, productType: ProductType.RING_PRO }
    : undefined;
}

function dieselSizingKit(store: ShopifyStore): RingSizingKitOption | undefined {
  // Diesel reuses the Ring AIR sizing kit product per
  // components/uh-x-diesel/buy/utils/checkout.tsx
  const id = SizingKitShopifyMap[store]?.shopifyVariantId;
  return typeof id === 'string' && id.length > 0
    ? { shopifyVariantId: id, productType: ProductType.RING }
    : undefined;
}

function rareSizingKit(store: ShopifyStore): RingSizingKitOption | undefined {
  const id = RareSizingKitShopifyMap[store]?.shopifyVariantId;
  return typeof id === 'string' && id.length > 0
    ? { shopifyVariantId: id, productType: ProductType.RING_RARE }
    : undefined;
}

function buildAirColors(store: ShopifyStore, t: TFunction): RingColorVariant[] {
  const colorMap = getRingAirColorNameImages(t);

  return getAvailableAirRingColors(store).flatMap((color): RingColorVariant[] => {
    // Source the price from RingPriceData (same as the /ring/buy page) rather
    // than pricingMap_air_ring_PROD, which only wires IN/AE/ROW/US/EU/GB and is
    // never alias-expanded — so AU/MX/SA/ZA/CA would otherwise resolve to no
    // price and drop every color, leaving the shop ring/pack picker empty.
    const priceRaw = RingPriceData?.[store]?.[color]?.price;
    const price = typeof priceRaw === 'number' ? priceRaw : undefined;
    if (price == null) return [];

    const sizeMap = productMap_air_ring[color];
    const sizes: RingSizeOption[] = Object.entries(sizeMap ?? {})
      .map(([sizeKey, byType]): RingSizeOption | null => {
        const variantId = byType?.[RingType.NOT_ENGRAVED]?.variantId?.[store];
        if (typeof variantId !== 'string' || variantId.length === 0)
          return null;
        const size = Number(sizeKey) as RingSizes;
        return {
          size,
          shopifyVariantId: variantId,
          isOpenSize: size === RingSizes.OPEN,
        };
      })
      .filter((s): s is RingSizeOption => s !== null)
      .sort((a, b) => a.size - b.size);
    if (sizes.length === 0) return [];

    return [
      {
        key: color,
        name: colorMap[color]?.name as string,
        swatch: colorMap[color]?.swatch as string,
        mainImage: colorMap[color]?.shop as string,
        price,
        sizes,
      },
    ];
  });
}

function buildProColors(
  store: ShopifyStore,
  currency: Currency,
  t: TFunction,
): RingColorVariant[] {
  const price = getRingProBasePrice(store, currency);
  if (price == null) return [];
  const colorMap = getRingProColorNameImages(t);

  return Object.values(RingProColors).flatMap((color): RingColorVariant[] => {
    const sizeMap = RingProVariants[color];
    const sizes: RingSizeOption[] = Object.entries(sizeMap ?? {})
      .map(([sizeKey, byType]): RingSizeOption | null => {
        const variantId = byType?.[RingType.NOT_ENGRAVED]?.variantId?.[store];
        if (typeof variantId !== 'string' || variantId.length === 0)
          return null;
        const size = Number(sizeKey) as RingSizes;
        return {
          size,
          shopifyVariantId: variantId,
          isOpenSize: size === RingSizes.OPEN,
        };
      })
      .filter((s): s is RingSizeOption => s !== null)
      .sort((a, b) => a.size - b.size);
    if (sizes.length === 0) return [];

    return [
      {
        key: color,
        name: colorMap[color].name,
        swatch: colorMap[color].swatch,
        mainImage: colorMap[color].shop,
        price,
        sizes,
      },
    ];
  });
}

function buildDieselColors(store: ShopifyStore): RingColorVariant[] {
  const byColor = DieselRingPriceData[store];
  if (!byColor) return [];

  return Object.values(DieselRingColors).flatMap(
    (color): RingColorVariant[] => {
      const price = byColor[color]?.price;
      if (typeof price !== 'number' || price <= 0) return [];

      const sizeMap = productMap_diesel_ring[color];
      const sizes: RingSizeOption[] = Object.entries(sizeMap ?? {})
        .map(([sizeKey, byType]): RingSizeOption | null => {
          const variantId =
            byType?.[DieselRingType.NOT_ENGRAVED]?.variantId?.[store];
          if (typeof variantId !== 'string' || variantId.length === 0)
            return null;
          const size = Number(sizeKey) as RingSizes;
          return {
            size,
            shopifyVariantId: variantId,
            isOpenSize: size === RingSizes.OPEN,
          };
        })
        .filter((s): s is RingSizeOption => s !== null)
        .sort((a, b) => a.size - b.size);
      if (sizes.length === 0) return [];

      return [
        {
          key: color,
          name: DieselRingColorNameImages[color]?.name as string,
          swatch: DieselRingColorNameImages[color]?.image as string,
          mainImage: DieselRingColorNameLargeImages[color]?.shop as string,
          price,
          sizes,
        },
      ];
    },
  );
}

function buildRareColors(store: ShopifyStore): RingColorVariant[] {
  const byColor = RarePriceData[store];
  if (!byColor) return [];

  return Object.values(RareColors).flatMap((color): RingColorVariant[] => {
    const price = byColor[color]?.price;
    if (typeof price !== 'number' || price <= 0) return [];

    const sizeMap = productMap_rare[color];
    const sizes: RingSizeOption[] = Object.entries(sizeMap ?? {})
      .map(([sizeKey, variant]): RingSizeOption | null => {
        const variantId = variant?.variantId?.[store];
        if (typeof variantId !== 'string' || variantId.length === 0)
          return null;
        const size = Number(sizeKey) as RingSizes;
        return {
          size,
          shopifyVariantId: variantId,
          isOpenSize: size === RingSizes.OPEN,
        };
      })
      .filter((s): s is RingSizeOption => s !== null)
      .sort((a, b) => a.size - b.size);
    if (sizes.length === 0) return [];

    return [
      {
        key: color,
        name: RareColorNameImages[color]?.name as string,
        swatch: RareColorNameImages[color]?.image as string,
        mainImage: RareColorNameImages[color]?.shop as string,
        price,
        sizes,
      },
    ];
  });
}

// Swatches reuse the Ring PRO color dots (aster-black / bionic-gold).
const RING_PRO_CHARGING_CASE_SOURCES = [
  {
    key: 'black',
    name: 'Black',
    swatch: 'web_v2/ring-buy/ring-air-color-swatch/aster-black.png',
    mainImage: 'web_v2/shop/addons/dark-charger.png',
    map: RingProChargingCaseBlackShopifyMap,
  },
  {
    key: 'gold',
    name: 'Gold',
    swatch: 'web_v2/ring-buy/ring-air-color-swatch/bionic-gold.png',
    mainImage: 'web_v2/shop/addons/gold-charger.png',
    map: RingProChargingCaseGoldShopifyMap,
  },
];

export function buildRingProChargingCaseColors(
  store: ShopifyStore,
): ColorAccessoryVariant[] {
  return RING_PRO_CHARGING_CASE_SOURCES.flatMap(
    (source): ColorAccessoryVariant[] => {
      // Self-hide guard: stores without config render nothing.
      const storeConfig = source.map[store];
      const price = storeConfig?.price;
      const id = storeConfig?.shopifyVariantId;
      if (typeof price !== 'number' || price <= 0) return [];
      if (typeof id !== 'string' || id.length === 0) return [];

      return [
        {
          key: source.key,
          name: source.name,
          swatch: source.swatch,
          mainImage: source.mainImage,
          price,
          shopifyVariantId: id,
        },
      ];
    },
  );
}

function lowestPrice(colors: RingColorVariant[]): number {
  return Math.min(...colors.map((c) => c.price));
}

export function buildShopRingCards(
  store: ShopifyStore,
  currency: Currency,
  t: TFunction,
): ProductCardRingProps[] {
  const cards: ProductCardRingProps[] = [];

  const proColors = buildProColors(store, currency, t);
  if (proColors.length > 0) {
    cards.push({
      variant: 'pro',
      productType: ProductType.RING_PRO,
      title: 'Ultrahuman Ring PRO',
      description: PRO_DESCRIPTION,
      fromPrice: lowestPrice(proColors),
      buyHref: '/ring-pro/buy',
      colors: proColors,
      sizingKit: proSizingKit(store),
      hasDedicatedUpsell: false,
      tag: 'NEW',
    });
  }

  const airColors = buildAirColors(store, t);
  if (airColors.length > 0 && store !== ShopifyStore.US) {
    cards.push({
      variant: 'air',
      productType: ProductType.RING,
      title: 'Ultrahuman Ring AIR',
      description: AIR_DESCRIPTION,
      fromPrice: lowestPrice(airColors),
      buyHref: '/ring/buy',
      colors: airColors,
      sizingKit: airSizingKit(store),
      hasDedicatedUpsell: true,
    });
  }

  const rareColors = buildRareColors(store);
  if (rareColors.length > 0 && store !== ShopifyStore.US) {
    cards.push({
      variant: 'rare',
      productType: ProductType.RING_RARE,
      title: 'Ultrahuman Ring RARE',
      description: RARE_DESCRIPTION,
      fromPrice: lowestPrice(rareColors),
      buyHref: '/rare/buy',
      colors: rareColors,
      sizingKit: rareSizingKit(store),
      hasDedicatedUpsell: true,
    });
  }

  const dieselColors = buildDieselColors(store);
  if (dieselColors.length > 0 && store !== ShopifyStore.US) {
    cards.push({
      variant: 'diesel',
      productType: ProductType.RING_DIESEL,
      title: 'Diesel Ultrahuman Ring',
      description: DIESEL_DESCRIPTION,
      fromPrice: lowestPrice(dieselColors),
      buyHref: '/diesel-ultrahuman-ring/buy',
      colors: dieselColors,
      sizingKit: dieselSizingKit(store),
      hasDedicatedUpsell: false,
    });
  }

  return cards;
}
